import React, { useCallback, useEffect, useRef, useState } from "react";

const MIN_W = 200;
const MIN_H = 140;
const DEFAULT_W = 256;
const DEFAULT_H = 176;

const SIZE_PRESETS = [
  { id: "small", label: "S", w: 220, h: 150 },
  { id: "medium", label: "M", w: 320, h: 220 },
  { id: "large", label: "L", w: 460, h: 300 },
];

/**
 * InteractivePipDemo — live PiP simulator on the LandingPage.
 *
 * Behaviors:
 *  - Drag the title bar to move the window (offset-anchored, so it doesn't snap).
 *  - Resize via the bottom-right corner handle.
 *  - Pause/Play the looping Houston video from the title bar.
 *  - Pop-out (fullscreen) the PiP via the title bar.
 *  - Switch demo content (Video / Meeting / Chat) via the bottom toggle bar.
 *  - Apply Small/Medium/Large size presets with one click.
 *
 * All interaction is pointer-events based (mouse + touch).
 */
export default function InteractivePipDemo() {
  const containerRef = useRef(null);
  const pipRef = useRef(null);
  const videoRef = useRef(null);

  const [pos, setPos] = useState({ x: 40, y: 40 });
  const [size, setSize] = useState({ w: DEFAULT_W, h: DEFAULT_H });
  const [content, setContent] = useState("video");
  const [isPlaying, setIsPlaying] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // ---- Drag (move) ----
  const dragOffset = useRef({ dx: 0, dy: 0 });
  const dragging = useRef(false);

  const clampPosition = useCallback((x, y, w, h) => {
    const c = containerRef.current;
    if (!c) return { x, y };
    const r = c.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(r.width - w, x)),
      y: Math.max(0, Math.min(r.height - h, y)),
    };
  }, []);

  const onTitleBarPointerDown = (e) => {
    if (isFullscreen) return;
    const rect = pipRef.current.getBoundingClientRect();
    dragOffset.current = { dx: e.clientX - rect.left, dy: e.clientY - rect.top };
    dragging.current = true;
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onTitleBarPointerMove = (e) => {
    if (!dragging.current || isFullscreen) return;
    const c = containerRef.current.getBoundingClientRect();
    const nx = e.clientX - c.left - dragOffset.current.dx;
    const ny = e.clientY - c.top - dragOffset.current.dy;
    setPos(clampPosition(nx, ny, size.w, size.h));
  };

  const onTitleBarPointerUp = (e) => {
    dragging.current = false;
    try { e.currentTarget.releasePointerCapture?.(e.pointerId); } catch { /* not captured */ }
  };

  // ---- Resize ----
  const resizing = useRef(false);
  const resizeStart = useRef({ x: 0, y: 0, w: 0, h: 0 });

  const onResizePointerDown = (e) => {
    if (isFullscreen) return;
    e.stopPropagation();
    resizing.current = true;
    resizeStart.current = { x: e.clientX, y: e.clientY, w: size.w, h: size.h };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onResizePointerMove = (e) => {
    if (!resizing.current || isFullscreen) return;
    const c = containerRef.current.getBoundingClientRect();
    const maxW = c.width - pos.x;
    const maxH = c.height - pos.y;
    const nw = Math.max(MIN_W, Math.min(maxW, resizeStart.current.w + (e.clientX - resizeStart.current.x)));
    const nh = Math.max(MIN_H, Math.min(maxH, resizeStart.current.h + (e.clientY - resizeStart.current.y)));
    setSize({ w: nw, h: nh });
  };

  const onResizePointerUp = (e) => {
    resizing.current = false;
    try { e.currentTarget.releasePointerCapture?.(e.pointerId); } catch { /* not captured */ }
  };

  // ---- Size presets ----
  const applyPreset = (p) => {
    if (isFullscreen) return;
    const c = containerRef.current;
    if (!c) return;
    const r = c.getBoundingClientRect();
    const w = Math.min(p.w, r.width);
    const h = Math.min(p.h, r.height);
    setSize({ w, h });
    setPos((cur) => clampPosition(cur.x, cur.y, w, h));
  };

  // ---- Play / Pause ----
  const togglePlay = (e) => {
    e.stopPropagation();
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play().catch(() => {}); } else { v.pause(); }
  };

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    v.addEventListener("play", onPlay);
    v.addEventListener("pause", onPause);
    return () => {
      v.removeEventListener("play", onPlay);
      v.removeEventListener("pause", onPause);
    };
  }, [content]); // re-attach when video element re-mounts

  // ---- Fullscreen (pop-out) ----
  const togglePopout = (e) => {
    e.stopPropagation();
    const el = pipRef.current;
    if (!el) return;
    if (document.fullscreenElement === el) {
      document.exitFullscreen?.();
    } else {
      el.requestFullscreen?.().catch(() => {});
    }
  };

  useEffect(() => {
    const onFsChange = () => setIsFullscreen(document.fullscreenElement === pipRef.current);
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  // ---- Render ----
  const fsStyle = isFullscreen
    ? { left: 0, top: 0, width: "100%", height: "100%" }
    : { left: pos.x, top: pos.y, width: size.w, height: size.h };

  return (
    <div
      ref={containerRef}
      className="relative w-full h-[500px] card-glass overflow-hidden"
      data-testid="pip-demo-container"
    >
      {/* Faux workspace behind */}
      <div className="absolute inset-0 p-6 pointer-events-none">
        <div className="bg-white/5 rounded-lg p-4 h-full">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-3 h-3 rounded-full bg-red-500" />
            <div className="w-3 h-3 rounded-full bg-yellow-500" />
            <div className="w-3 h-3 rounded-full bg-green-500" />
            <span className="text-sm ml-2 neon-text-white">Your Workspace</span>
          </div>
          <div className="space-y-3">
            <div className="h-4 bg-white/10 rounded w-3/4" />
            <div className="h-4 bg-white/10 rounded w-1/2" />
            <div className="h-4 bg-white/10 rounded w-5/6" />
            <div className="h-32 bg-white/5 rounded mt-4" />
            <div className="h-4 bg-white/10 rounded w-2/3" />
            <div className="h-4 bg-white/10 rounded w-1/3" />
          </div>
        </div>
      </div>

      {/* The PiP window */}
      <div
        ref={pipRef}
        className="absolute group"
        style={fsStyle}
        data-testid="pip-demo-window"
      >
        <div className={`absolute -inset-1 bg-gradient-to-r from-[#A3E635] to-[#4B3F8F] rounded-lg opacity-75 blur-sm ${isFullscreen ? "hidden" : ""}`} />
        <div className="relative bg-black rounded-lg overflow-hidden border-2 border-[#A3E635]/50 h-full w-full">
          {/* Title bar */}
          <div
            className="bg-gradient-to-r from-[#001b3d] to-[#298203] px-2 py-1 flex items-center justify-between cursor-move select-none touch-none"
            onPointerDown={onTitleBarPointerDown}
            onPointerMove={onTitleBarPointerMove}
            onPointerUp={onTitleBarPointerUp}
            onPointerCancel={onTitleBarPointerUp}
            data-testid="pip-demo-titlebar"
          >
            <span className="text-[#A3E635] text-xs font-bold pointer-events-none">Top Screen&#8482;</span>
            <div className="flex items-center gap-1.5">
              {content === "video" && (
                <button
                  type="button"
                  onClick={togglePlay}
                  onPointerDown={(e) => e.stopPropagation()}
                  className="w-5 h-5 rounded flex items-center justify-center text-white hover:bg-white/15 transition-colors"
                  aria-label={isPlaying ? "Pause video" : "Play video"}
                  title={isPlaying ? "Pause" : "Play"}
                  data-testid="pip-demo-playpause"
                >
                  {isPlaying ? (
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="5" width="4" height="14" rx="1" /><rect x="14" y="5" width="4" height="14" rx="1" /></svg>
                  ) : (
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5l12 7-12 7V5z" /></svg>
                  )}
                </button>
              )}
              <button
                type="button"
                onClick={togglePopout}
                onPointerDown={(e) => e.stopPropagation()}
                className="w-5 h-5 rounded flex items-center justify-center text-white hover:bg-white/15 transition-colors"
                aria-label={isFullscreen ? "Exit fullscreen" : "Pop out"}
                title={isFullscreen ? "Exit fullscreen" : "Pop out (fullscreen)"}
                data-testid="pip-demo-popout"
              >
                {isFullscreen ? (
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 3v4a2 2 0 01-2 2H3M21 9h-4a2 2 0 01-2-2V3M15 21v-4a2 2 0 012-2h4M3 15h4a2 2 0 012 2v4" />
                  </svg>
                ) : (
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 9V5a2 2 0 012-2h4M15 3h4a2 2 0 012 2v4M21 15v4a2 2 0 01-2 2h-4M9 21H5a2 2 0 01-2-2v-4" />
                  </svg>
                )}
              </button>
              <span className="w-2 h-2 rounded-full bg-yellow-500" />
              <span className="w-2 h-2 rounded-full bg-green-500" />
            </div>
          </div>

          {/* Body */}
          <div className="h-[calc(100%-26px)] bg-black/80 relative overflow-hidden">
            {content === "video" && (
              <video
                ref={videoRef}
                className="absolute inset-0 w-full h-full object-cover"
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
                data-testid="pip-demo-video"
              >
                <source src="/videos/houston-demo.webm" type="video/webm" />
                <source src="/videos/houston-demo.mp4" type="video/mp4" />
              </video>
            )}
            {content === "meeting" && (
              <div className="w-full h-full flex flex-col items-center justify-center">
                <div className="w-12 h-12 mb-2 rounded-full bg-[#4B3F8F]/30 flex items-center justify-center">
                  <span className="text-2xl">&#128100;</span>
                </div>
                <p className="text-[#E8E4DC] text-xs">Zoom Meeting</p>
              </div>
            )}
            {content === "chat" && (
              <div className="w-full h-full flex flex-col items-center justify-center px-2">
                <div className="flex items-end justify-center gap-1 h-12 mb-2">
                  {[60, 80, 40, 90, 70].map((h, i) => (
                    <div key={i} className="w-3 bg-[#A3E635] rounded-t" style={{ height: `${h}%` }} />
                  ))}
                </div>
                <p className="text-[#E8E4DC] text-xs">Live Chat</p>
              </div>
            )}
          </div>

          {/* Resize handle */}
          {!isFullscreen && (
            <div
              className="absolute bottom-0 right-0 w-4 h-4 cursor-nwse-resize touch-none flex items-end justify-end p-0.5"
              onPointerDown={onResizePointerDown}
              onPointerMove={onResizePointerMove}
              onPointerUp={onResizePointerUp}
              onPointerCancel={onResizePointerUp}
              aria-label="Resize"
              title="Drag to resize"
              data-testid="pip-demo-resize"
            >
              <svg width="10" height="10" viewBox="0 0 10 10" className="text-[#A3E635]/80">
                <path d="M0 10 L10 0 M4 10 L10 4 M8 10 L10 8" stroke="currentColor" strokeWidth="1.2" fill="none" />
              </svg>
            </div>
          )}
        </div>
        {/* Hover tooltip — child of the `group` PiP wrapper so group-hover triggers
            when the user mouses over the window. Hidden by default; fades in smoothly.
            pointer-events-none so it never blocks drag / click on title bar or corner. */}
        <div
          className="absolute top-2 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-md text-[8px] whitespace-nowrap pointer-events-none neon-text-white bg-black/80 backdrop-blur-sm border border-white/15 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-50"
          data-testid="pip-demo-tooltip"
        >
          &#8597; Drag the title bar &nbsp;&middot;&nbsp; pop-out &nbsp;&middot;&nbsp; re-size the corner &#8596;
        </div>
      </div>

      {/* Content switcher + size presets — all on one row */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-3 flex-wrap justify-center">
        <div className="flex gap-2">
          {["video", "meeting", "chat"].map((type) => (
            <button
              key={type}
              onClick={() => setContent(type)}
              className={`px-3 py-1 rounded-full text-xs font-semibold transition-all capitalize ${content === type ? "bg-[#A3E635] text-black" : "bg-white/10 text-white hover:bg-white/20"}`}
              data-testid={`demo-btn-${type}`}
            >
              {type}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          {SIZE_PRESETS.map((p) => {
            const active = size.w === p.w && size.h === p.h;
            return (
              <button
                key={p.id}
                onClick={() => applyPreset(p)}
                className={`w-7 h-7 rounded-full text-[11px] font-bold transition-all border ${active ? "bg-[#A3E635] text-black border-[#A3E635]" : "bg-white/5 text-white border-white/15 hover:bg-white/15"}`}
                title={`${p.id} (${p.w}×${p.h})`}
                aria-label={`Size ${p.id}`}
                data-testid={`demo-size-${p.id}`}
              >
                {p.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
